import * as React from "react";
import { Link } from "react-router-dom";
import { useAction, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { WORKFORCE_PERSONAS } from "../../../../../convex/aiWorkforce/config";
import { getPersonaTheme, formatToolkitName } from "./personaTheme";
import { AgentRoutinesModal } from "./AgentRoutinesModal";
import { AgentUsageSheet } from "./AgentUsageSheet";
import {
  Bot,
  Plug,
  ExternalLink,
  ArrowRight,
  MessageSquare,
  ShieldCheck,
  Calendar,
  Clock,
  Zap,
  TrendingUp,
} from "lucide-react";

interface PaneAgentsOverviewProps {
  onSelectPersonaChannel: (personaKey: string) => void;
  onOpenAgentUsage?: (personaKey: string) => void;
  orgId?: string;
  personalWorkspaceId?: string;
}

export function PaneAgentsOverview({
  onSelectPersonaChannel,
  onOpenAgentUsage,
  orgId,
  personalWorkspaceId,
}: PaneAgentsOverviewProps) {
  const [connections, setConnections] = React.useState<any[]>([]);
  const [isLoadingConnections, setIsLoadingConnections] = React.useState(true);
  const [selectedRoutinesPersonaKey, setSelectedRoutinesPersonaKey] = React.useState<string | null>(null);
  const [selectedUsagePersonaKey, setSelectedUsagePersonaKey] = React.useState<string | null>(null);
  const listConnectionsAction = useAction(api.connections.list);

  const roiMetrics = useQuery(api.aiWorkforce.roiMetrics.getWorkforceUsageAndROI, {
    days: 30,
    orgId: orgId as any,
    personalWorkspaceId: personalWorkspaceId as any,
  });

  React.useEffect(() => {
    let isMounted = true;
    listConnectionsAction({})
      .then((res) => {
        if (isMounted) {
          setConnections(res || []);
          setIsLoadingConnections(false);
        }
      })
      .catch((err) => {
        console.warn("Could not load user connections:", err);
        if (isMounted) setIsLoadingConnections(false);
      });
    return () => {
      isMounted = false;
    };
  }, [listConnectionsAction]);

  // Set of connected platform slugs
  const connectedSlugs = React.useMemo(() => {
    return new Set(
      connections
        .filter((c) => c.active)
        .map((c) => (c.platform || c.name || "").toLowerCase())
    );
  }, [connections]);

  const allPersonas = Object.values(WORKFORCE_PERSONAS);

  return (
    <div className="w-full h-full min-h-0 min-w-0 bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
      {/* Header Banner */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-slate-50 via-white to-blue-50/50 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="p-2.5 rounded-xl bg-blue-100 text-blue-700 shadow-2xs">
                <Bot className="w-7 h-7" />
              </span>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                  Operations Team & Standing Routines
                </h2>
                <p className="text-sm sm:text-base text-gray-500 mt-1">
                  Meet your specialized commercial cleaning assistants, configure their recurring routines, and assign tasks.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/settings/connections"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold shadow-sm transition-colors"
            >
              <span>Manage Connected Apps</span>
              <ExternalLink className="w-4 h-4 opacity-80" />
            </Link>
          </div>
        </div>
      </div>

      {/* 2-Column Spacious Grid */}
      <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-50/60">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 max-w-7xl mx-auto">
          {allPersonas.map((persona) => {
            const theme = getPersonaTheme(persona.key);
            const agentToolkits = persona.composioToolkits || [];
            const activeToolkitsForAgent = agentToolkits.filter((slug) =>
              connectedSlugs.has(slug.toLowerCase())
            );
            const routines = persona.routines || [];
            const personaMetric = roiMetrics?.agentBreakdown?.find(
              (a: any) => a.personaKey === persona.key
            );

            return (
              <div
                key={persona.key}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all flex flex-col justify-between overflow-hidden"
              >
                {/* Agent Card Top Section */}
                <div>
                  {/* Agent Header */}
                  <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div
                        className={`w-12 h-12 rounded-2xl ${theme.bgLight} border ${theme.border} ${theme.text} flex items-center justify-center flex-shrink-0 shadow-xs`}
                      >
                        <Bot className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {persona.name}
                        </h3>
                        <p className="text-sm font-medium text-gray-500 mt-0.5">
                          {persona.title}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {personaMetric && personaMetric.creditsUsed > 0 && (
                        <button
                          onClick={() => {
                            if (onOpenAgentUsage) onOpenAgentUsage(persona.key);
                            else setSelectedUsagePersonaKey(persona.key);
                          }}
                          className="text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 transition-colors shadow-2xs"
                          title="Click to view usage and estimated hours saved"
                        >
                          <Zap className="w-3 h-3 text-amber-600" />
                          <span>{personaMetric.creditsUsed} pts</span>
                          <span className="text-amber-400">•</span>
                          <span className="text-emerald-700 font-bold">{personaMetric.estimatedHoursSaved}h saved</span>
                        </button>
                      )}

                      {routines.length > 0 ? (
                        <button
                          onClick={() => setSelectedRoutinesPersonaKey(persona.key)}
                          className="text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition-colors"
                          title="Click to view & configure routines"
                        >
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{routines.length} Routine{routines.length > 1 ? "s" : ""}</span>
                        </button>
                      ) : (
                        <span className="text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1.5 bg-gray-100 text-gray-600 border border-gray-200">
                          On-Demand
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Content Body */}
                  <div className="p-5 space-y-4">
                    {/* Role & Responsibilities */}
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5 flex items-center gap-1.5">
                        <ShieldCheck className="w-4 h-4 text-blue-600" />
                        <span>Role & Responsibilities</span>
                      </div>
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                        {persona.description}
                      </p>
                    </div>

                    {/* Recommended Integrations / App Connections */}
                    <div className="bg-gray-50/80 rounded-xl p-4 border border-gray-200/80 space-y-2.5">
                      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <Plug className="w-4 h-4 text-blue-600" />
                          <span>Recommended Connections</span>
                        </span>
                        <span className="text-xs font-medium text-gray-500">
                          <span className="text-emerald-700 font-semibold">{activeToolkitsForAgent.length} connected</span> of {agentToolkits.length}
                        </span>
                      </div>

                      {agentToolkits.length > 0 ? (
                        <div className="flex flex-wrap gap-2 pt-0.5">
                          {agentToolkits.map((slug) => {
                            const isConnected = connectedSlugs.has(slug.toLowerCase());
                            const label = formatToolkitName(slug);

                            return (
                              <Link
                                key={slug}
                                to="/settings/connections"
                                title={isConnected ? `${label}: Connected` : `${label}: Not connected (Click to configure)`}
                                className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border font-medium transition-all ${
                                  isConnected
                                    ? "bg-emerald-50 text-emerald-900 border-emerald-200 hover:bg-emerald-100 shadow-2xs"
                                    : "bg-gray-100/90 text-gray-600 border-gray-200 hover:bg-gray-200/70"
                                }`}
                              >
                                <span
                                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    isConnected ? "bg-emerald-500 ring-2 ring-emerald-200" : "bg-gray-400"
                                  }`}
                                />
                                <span>{label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 italic mt-0.5">
                          Connected directly to your ManageClean database records.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card Footer Actions */}
                <div className="p-4 bg-gray-50/90 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2.5">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedRoutinesPersonaKey(persona.key)}
                      className="text-xs sm:text-sm font-semibold text-gray-700 hover:text-blue-700 px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all flex items-center gap-1.5 shadow-2xs"
                    >
                      <Clock className="w-3.5 h-3.5 text-blue-600" />
                      <span>Routines ({routines.length})</span>
                    </button>

                    <button
                      onClick={() => {
                        if (onOpenAgentUsage) onOpenAgentUsage(persona.key);
                        else setSelectedUsagePersonaKey(persona.key);
                      }}
                      className="text-xs sm:text-sm font-semibold text-gray-700 hover:text-amber-700 px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-amber-50/60 hover:border-amber-300 transition-all flex items-center gap-1.5 shadow-2xs"
                    >
                      <Zap className="w-3.5 h-3.5 text-amber-500" />
                      <span>Usage & ROI</span>
                    </button>
                  </div>

                  <button
                    onClick={() => onSelectPersonaChannel(persona.key)}
                    className="text-xs sm:text-sm font-semibold px-3.5 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors flex items-center gap-1.5"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>Chat</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Direct 1-Click Agent Routines Modal */}
      <AgentRoutinesModal
        isOpen={!!selectedRoutinesPersonaKey}
        onClose={() => setSelectedRoutinesPersonaKey(null)}
        personaKey={selectedRoutinesPersonaKey}
        onOpenChat={onSelectPersonaChannel}
      />

      {/* Direct 1-Click Agent Usage & ROI Sheet */}
      <AgentUsageSheet
        isOpen={!!selectedUsagePersonaKey}
        onClose={() => setSelectedUsagePersonaKey(null)}
        personaKey={selectedUsagePersonaKey}
        orgId={orgId}
        personalWorkspaceId={personalWorkspaceId}
      />
    </div>
  );
}
